// client/src/App.tsx
// Root routing component for AGOC School.
//
// Stack: @ionic/react-router v8 → React Router v5
//
// Routing rules for @ionic/react-router:
//   - SINGLE IonRouterOutlet — never replace it conditionally based on auth state.
//     If you swap IonRouterOutlet instances, React reconciles them as the same component
//     type and merely updates children; Ionic's internal view stack stays pinned to the
//     current URL and <Redirect> never fires. A single outlet re-evaluates routes on
//     every render, so redirects fire correctly when auth state changes.
//   - Routes must be inside <Switch> so the path-less catch-all only fires as a fallback.
//   - Use <Redirect> from react-router-dom (v5), NOT <Navigate> (v6 API).
//
// Adaptations from PilotAcademyAI App.tsx:
//   - user.role (string) → user.roles (string[]) — all checks use .includes()
//   - needsRole → needsRoleAssignment
//   - 'dios' role removed (not a valid platform role)
//   - All pages wrapped in IonPage (via DashboardLayout)
//   - Static imports → React.lazy for code splitting
//   - Toaster moved to main.tsx

import React from 'react';
import { IonRouterOutlet } from '@ionic/react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

// ── Page imports (lazy) ────────────────────────────────────────────────────

const LoginPage       = React.lazy(() => import('@/pages/LoginPage'));
const NotFoundPage    = React.lazy(() => import('@/pages/NotFoundPage'));
const WaitingForRole  = React.lazy(() => import('@/pages/WaitingForRole'));

// Dashboard
const AdminDashboard   = React.lazy(() => import('@/pages/dashboard/AdminDashboard'));
const TeacherDashboard = React.lazy(() => import('@/pages/dashboard/TeacherDashboard'));
const StudentDashboard = React.lazy(() => import('@/pages/dashboard/StudentDashboard'));

// Admin
const CoursesPage           = React.lazy(() => import('@/pages/admin/CoursesPage'));
const SubjectsPage          = React.lazy(() => import('@/pages/admin/SubjectsPage'));
const UsersPage             = React.lazy(() => import('@/pages/admin/UsersPage'));
const EnrollmentsPage       = React.lazy(() => import('@/pages/admin/EnrollmentsPage'));
const SettingsPage          = React.lazy(() => import('@/pages/admin/SettingsPage'));
const StatisticsPage        = React.lazy(() => import('@/pages/admin/StatisticsPage'));
const SubmissionsReviewPage = React.lazy(() => import('@/pages/admin/SubmissionsReviewPage'));
const BulkImportPage        = React.lazy(() => import('@/pages/admin/BulkImportPage'));
const AIFeedbackPage        = React.lazy(() => import('@/pages/admin/AIFeedbackPage'));

// Teacher
const TeacherSubjectsPage = React.lazy(() => import('@/pages/teacher/TeacherSubjectsPage'));
const AIStatsPage         = React.lazy(() => import('@/pages/teacher/AIStatsPage'));

// Student
const StudentCoursesPage = React.lazy(() => import('@/pages/student/StudentCoursesPage'));
const AIChatPage         = React.lazy(() => import('@/pages/student/AIChatPage'));
const ChatHistoryPage    = React.lazy(() => import('@/pages/student/ChatHistoryPage'));
const QuizzesPage        = React.lazy(() => import('@/pages/student/QuizzesPage'));
const ProgressPage       = React.lazy(() => import('@/pages/student/ProgressPage'));

// Help (public)
const HelpPage    = React.lazy(() => import('@/pages/help/HelpPage'));
const HelpDocPage = React.lazy(() => import('@/pages/help/HelpDocPage'));

// Shared
const MaterialsPage = React.lazy(() => import('@/pages/materials/MaterialsPage'));
const CalendarPage  = React.lazy(() => import('@/pages/calendar/CalendarPage'));
const ProfilePage   = React.lazy(() => import('@/pages/ProfilePage'));

// Exams
const ExamsPage          = React.lazy(() => import('@/pages/exams/ExamsPage'));
const CreateExamPage     = React.lazy(() => import('@/pages/exams/CreateExamPage'));
const TakeExamPage       = React.lazy(() => import('@/pages/exams/TakeExamPage'));
const ReviewExamPage     = React.lazy(() => import('@/pages/exams/ReviewExamPage'));
const ExamResultsPage    = React.lazy(() => import('@/pages/exams/ExamResultsPage'));

// ── Loading screen ─────────────────────────────────────────────────────────

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Cargando...</p>
      </div>
    </div>
  );
}

// ── AppRoutes ─────────────────────────────────────────────────────────────
// Single IonRouterOutlet — auth guards are inline in each route's render fn.
// This ensures the outlet is never replaced, so Ionic's view stack is always
// live and <Redirect> fires immediately when auth state changes.

function AppRoutes() {
  const { user, loading, needsRoleAssignment, firebaseUser } = useAuth();

  // Show loading screen OUTSIDE IonRouterOutlet — this is the only case where
  // we don't render the outlet. It only fires once on cold start.
  if (loading) {
    return <LoadingScreen />;
  }

  // ── Role helpers ──────────────────────────────────────────────────────────
  const roles = user?.roles ?? [];
  const isTeacher = roles.includes('teacher') || roles.includes('teacher_admin');
  const isStudent = roles.includes('student');
  const isAdmin =
    roles.includes('admin') ||
    roles.includes('teacher_admin') ||
    roles.includes('platform_admin');
  const hasTeacherAccess = isTeacher || isAdmin;

  const dashboardPath = '/';

  // ── Auth guard helper ─────────────────────────────────────────────────────
  // Used by every protected route: redirects to login or waiting-for-role as needed.
  const guard = (el: JSX.Element): JSX.Element => {
    if (!user && !needsRoleAssignment && !firebaseUser) return <Redirect to="/login" />;
    if (needsRoleAssignment || (firebaseUser && !user)) return <Redirect to="/waiting-for-role" />;
    return el;
  };

  return (
    <IonRouterOutlet>
      <React.Suspense fallback={<LoadingScreen />}>
        <Switch>

          {/* ── Login ──────────────────────────────────────────────────────── */}
          {/* Redirect to dashboard if already authenticated. */}
          <Route
            exact
            path="/login"
            render={() => {
              if (needsRoleAssignment || (firebaseUser && !user)) return <Redirect to="/waiting-for-role" />;
              if (user) return <Redirect to={dashboardPath} />;
              return <LoginPage />;
            }}
          />

          {/* ── Help — requires authentication ───────────────────────────── */}
          <Route exact path="/help" render={() => guard(<HelpPage />)} />
          <Route
            path="/help/:doc"
            render={(props) => {
              const doc = (props.match.params as { doc: string }).doc;
              if (doc === 'admin-profesor' && !isTeacher && !isAdmin) {
                return <Redirect to="/help" />;
              }
              return guard(<HelpDocPage />);
            }}
          />

          {/* ── Waiting for role ───────────────────────────────────────────── */}
          <Route
            path="/waiting-for-role"
            render={() => {
              if (!firebaseUser) return <Redirect to="/login" />;
              if (user && user.roles.length > 0) return <Redirect to={dashboardPath} />;
              return <WaitingForRole />;
            }}
          />

          {/* ── Root — role-based dashboard ────────────────────────────────── */}
          <Route
            exact
            path="/"
            render={() =>
              guard(
                isAdmin
                  ? <AdminDashboard />
                  : isTeacher
                  ? <TeacherDashboard />
                  : <StudentDashboard />,
              )
            }
          />

          {/* ── Profile (all authenticated users) ─────────────────────────── */}
          <Route
            exact
            path="/profile"
            render={() => guard(<ProfilePage />)}
          />

          {/* ── Shared routes (teacher + student + admin) ──────────────────── */}
          <Route
            exact
            path="/materials"
            render={() =>
              guard(hasTeacherAccess || isStudent ? <MaterialsPage /> : <Redirect to="/" />)
            }
          />
          <Route
            exact
            path="/exams"
            render={() =>
              guard(hasTeacherAccess || isStudent ? <ExamsPage /> : <Redirect to="/" />)
            }
          />
          <Route
            exact
            path="/calendar"
            render={() =>
              guard(hasTeacherAccess || isStudent ? <CalendarPage /> : <Redirect to="/" />)
            }
          />
          <Route
            exact
            path="/statistics"
            render={() =>
              guard(hasTeacherAccess || isAdmin ? <StatisticsPage /> : <Redirect to="/" />)
            }
          />
          <Route
            exact
            path="/submissions-review"
            render={() =>
              guard(hasTeacherAccess ? <SubmissionsReviewPage /> : <Redirect to="/" />)
            }
          />

          {/* ── Teacher-only routes ────────────────────────────────────────── */}
          <Route
            exact
            path="/subjects"
            render={() => guard(isTeacher ? <TeacherSubjectsPage /> : <Redirect to="/" />)}
          />
          <Route
            exact
            path="/ai-stats"
            render={() => guard((isTeacher || isAdmin) ? <AIStatsPage /> : <Redirect to="/" />)}
          />
          <Route
            path="/exams/create"
            render={() => guard(hasTeacherAccess ? <CreateExamPage /> : <Redirect to="/" />)}
          />
          <Route
            path="/exams/edit/:id"
            render={() => guard(hasTeacherAccess ? <CreateExamPage /> : <Redirect to="/" />)}
          />
          <Route
            path="/exams/:id/results"
            render={() => guard(hasTeacherAccess ? <ExamResultsPage /> : <Redirect to="/" />)}
          />

          {/* ── Admin routes ───────────────────────────────────────────────── */}
          <Route
            path="/admin/courses"
            render={() => guard(isAdmin ? <CoursesPage /> : <Redirect to="/" />)}
          />
          <Route
            path="/admin/subjects"
            render={() => guard(isAdmin ? <SubjectsPage /> : <Redirect to="/" />)}
          />
          <Route
            path="/admin/users"
            render={() => guard(isAdmin ? <UsersPage /> : <Redirect to="/" />)}
          />
          <Route
            path="/admin/enrollments"
            render={() => guard(isAdmin ? <EnrollmentsPage /> : <Redirect to="/" />)}
          />
          <Route
            path="/admin/settings"
            render={() => guard(isAdmin ? <SettingsPage /> : <Redirect to="/" />)}
          />
          <Route
            path="/admin/statistics"
            render={() => guard((isAdmin || isTeacher) ? <StatisticsPage /> : <Redirect to="/" />)}
          />
          <Route
            path="/admin/submissions-review"
            render={() => guard(isAdmin ? <SubmissionsReviewPage /> : <Redirect to="/" />)}
          />
          <Route
            path="/admin/bulk-import"
            render={() => guard(isAdmin ? <BulkImportPage /> : <Redirect to="/" />)}
          />
          <Route
            path="/admin/ai-feedback"
            render={() => guard(isAdmin ? <AIFeedbackPage /> : <Redirect to="/" />)}
          />

          {/* ── Student-only routes ────────────────────────────────────────── */}
          <Route
            path="/courses"
            render={() => guard(isStudent ? <StudentCoursesPage /> : <Redirect to="/" />)}
          />
          <Route
            path="/ai-chat"
            render={() => guard(isStudent ? <AIChatPage /> : <Redirect to="/" />)}
          />
          <Route
            path="/chat-history"
            render={() => guard(isStudent ? <ChatHistoryPage /> : <Redirect to="/" />)}
          />
          <Route
            path="/quizzes"
            render={() => guard(isStudent ? <QuizzesPage /> : <Redirect to="/" />)}
          />
          <Route
            path="/progress"
            render={() => guard(isStudent ? <ProgressPage /> : <Redirect to="/" />)}
          />
          <Route
            path="/exams/:id/take"
            render={() => guard(isStudent ? <TakeExamPage /> : <Redirect to="/" />)}
          />
          <Route
            path="/exams/:id/review"
            render={() => guard(isStudent ? <ReviewExamPage /> : <Redirect to="/" />)}
          />

          {/* ── 404 catch-all ──────────────────────────────────────────────── */}
          <Route component={NotFoundPage} />

        </Switch>
      </React.Suspense>
    </IonRouterOutlet>
  );
}

// ── App ────────────────────────────────────────────────────────────────────
export default function App(): React.ReactElement {
  return <AppRoutes />;
}
